import React, { useEffect, useMemo, useState, ChangeEvent } from 'react';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import ButtonLoading from '@mui/lab/LoadingButton';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { Autocomplete, Box, Chip } from '@mui/material';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import { ReactComponent as CubeAccrossIcon } from 'assets/icons/icon-cube-across.svg';
import { ReactComponent as CubeDown } from 'assets/icons/icon-cube-down.svg';
import { useGetAdditionalWorkFlowsDropdown } from 'services/v1/Tenant/ConfigurationService';
import { PeopleMyViewItemConfig, RequestSaveMyViewAdditionalConfig } from 'types/api/Tenant/ConfigurationTypes';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import { flattenTreeData, renderOptionsKtree, getListBoxPropsAutoScrolItem } from 'utils/Object';
import {
  INSIGHT_VIEW_MEASUREMENT_TYPE,
  INSIGHT_VIEW_MEASURE_TYPE_DROPDOWN_OPTIONS,
  getInsightViewDimensionFilterLabel,
  getInsightViewMeasureTypeDropdowns,
} from 'constant/ViewConstant';
import { useGetCubeDimensionalViewFilterMember } from 'services/v1/Tenant/AROCube/CubeDimensionalViewService';
import { DIMENSION_CATEGORY } from 'types/api/Tenant/AROCube/CubeDataDimensionalViewTypes';
import { useGetKtreeData } from 'services/v1/Tenant/AROKMS/KTreeDataService';
import { useGetChartViewPanelOptions } from 'services/v1/Tenant/AROChart/ChartViewService';
import { NAVIGATION_COMPONENT_TYPE } from 'constant/NavigationConstant';
import { useGetTableAnalyticOptions } from 'services/v1/SystemTenant/AROKMS/TableAnalyticService';

const ModalContentStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 550,
  bgcolor: 'background.paper',
  minHeight: 400,
  maxHeight: 800,
  overflow: 'auto',
  borderRadius: 1,
  py: 2,
};

const autoCompleteStyle = {
  width: 230,
  '& .MuiAutocomplete-popper': {
    fontSize: '10px',
  },
};

const closeIconStyle = { color: '#98A2AE', cursor: 'pointer' };

export interface ModalMyViewAdditionalConfigProps {
  visible?: boolean;
  onClose?: () => void;
  onSubmit: (data: RequestSaveMyViewAdditionalConfig) => void;
  selectedView: PeopleMyViewItemConfig | null;
  defaultXDimensionTypeId: string | null;
  defaultYDimensionTypeId: string | null;
  defaultFilterMemberId: string | null;
}

interface DefaultFilterDimensionValue {
  defaultXDimensionType: AutoCompleteItem | null;
  defaultYDimensionType: AutoCompleteItem | null;
  defaultFilterMember: AutoCompleteItem | null;
  member: AutoCompleteItem | null;
  additionalWorkFlows: AutoCompleteItem | null;
  defaultMeasurement: AutoCompleteItem | null;
  currentPeriodOffset: number | null;
  tableAnalyticDefaultRow: AutoCompleteItem | null;
  tableAnalyticDefaultColumn: AutoCompleteItem | null;
}

const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: AutoCompleteItem) => (
  <Box component='li' sx={{ '& > span': { fontSize: '14px', mr: 1, flexShrink: 0 } }} {...props}>
    <span>{option.label}</span>
  </Box>
);

interface NonChartSettingsProps {
  selectedView: PeopleMyViewItemConfig | null;
  optionValue: DefaultFilterDimensionValue;
  updateField: <K extends keyof DefaultFilterDimensionValue>(key: K, value: DefaultFilterDimensionValue[K]) => void;
  memberOptions: AutoCompleteItem[];
  additionalWorkFlowsDropdown: any;
  xDimensionOptions: AutoCompleteItem[];
  yDimensionOptions: AutoCompleteItem[];
  dimensionMemberOptions: AutoCompleteItem[];
  isFetchingMember: boolean;
  isFetchingDimensionMember: boolean;
  lowestMemberLevel: number;
  lowestDimensionMemberLevel: number;
  dimensionFilterLabel: string;
  showDefaultDimensionMemberFilter: boolean;
}

const NonChartSettings: React.FC<NonChartSettingsProps> = ({
  selectedView,
  optionValue,
  updateField,
  memberOptions,
  additionalWorkFlowsDropdown,
  xDimensionOptions,
  yDimensionOptions,
  dimensionMemberOptions,
  isFetchingMember,
  isFetchingDimensionMember,
  lowestMemberLevel,
  lowestDimensionMemberLevel,
  dimensionFilterLabel,
  showDefaultDimensionMemberFilter,
}) => (
  <Stack gap={2.4}>
    {/* Member selection */}
    <Stack>
      <Typography variant='input-label-gray' mb={1}>
        {`Select a member as default filter for ${selectedView?.componentName}.`}
      </Typography>
      <Autocomplete
        onChange={(event, value) => updateField('member', value)}
        clearIcon={null}
        size='small'
        value={optionValue.member}
        getOptionLabel={(option: AutoCompleteItem) => option.label}
        options={memberOptions}
        renderOption={(props, option, state) => renderOptionsKtree(props, option, state, lowestMemberLevel)}
        ListboxProps={{
          ...getListBoxPropsAutoScrolItem(memberOptions, optionValue.member),
        }}
        renderInput={(params) => (
          <TextField {...params} label={isFetchingMember ? 'Loading...' : 'Member'} name='member' />
        )}
      />
    </Stack>

    {/* Additional drill down cube */}
    <Stack>
      <Typography variant='input-label-gray' mb={1}>
        {`Select a additional drill down cube for ${selectedView?.componentName}.`}
      </Typography>
      <Autocomplete
        onChange={(event, value) => updateField('additionalWorkFlows', value)}
        size='small'
        value={optionValue.additionalWorkFlows}
        getOptionLabel={(option: AutoCompleteItem) => option.label}
        options={additionalWorkFlowsDropdown?.data || []}
        renderOption={renderOption}
        renderInput={(params) => (
          <TextField {...params} label={isFetchingMember ? 'Loading...' : 'Additional Cube'} name='cube' />
        )}
      />
    </Stack>

    {/* Dimension filter settings */}
    <Stack>
      <Typography variant='input-label-gray' mb={2}>
        {`Select the default dimension filter for ${selectedView?.componentName}.`}
      </Typography>
      <Stack direction='row' gap={1}>
        <Autocomplete
          onChange={(event, value) => updateField('defaultXDimensionType', value)}
          clearIcon={null}
          size='small'
          value={optionValue.defaultXDimensionType}
          getOptionLabel={(option: AutoCompleteItem) => option.label}
          options={xDimensionOptions}
          renderOption={renderOption}
          sx={autoCompleteStyle}
          renderInput={(params) => (
            <TextField
              {...params}
              label='Down'
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position='start'>
                    <CubeDown />
                  </InputAdornment>
                ),
              }}
              name='xDimensionType'
            />
          )}
        />
        <Autocomplete
          onChange={(event, value) => updateField('defaultYDimensionType', value)}
          clearIcon={null}
          size='small'
          value={optionValue.defaultYDimensionType}
          getOptionLabel={(option: AutoCompleteItem) => option.label}
          options={yDimensionOptions}
          renderOption={renderOption}
          sx={autoCompleteStyle}
          renderInput={(params) => (
            <TextField
              {...params}
              label='Across'
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position='start'>
                    <CubeAccrossIcon />
                  </InputAdornment>
                ),
              }}
              name='yDimenstionType'
            />
          )}
        />
      </Stack>
      {showDefaultDimensionMemberFilter && (
        <Stack mt={3}>
          <Autocomplete
            onChange={(event, value) => updateField('defaultFilterMember', value)}
            clearIcon={null}
            size='small'
            value={optionValue.defaultFilterMember}
            getOptionLabel={(option: AutoCompleteItem) => option.label}
            options={dimensionMemberOptions}
            renderOption={(props, option, state) =>
              renderOptionsKtree(props, option, state, lowestDimensionMemberLevel)
            }
            ListboxProps={{
              ...getListBoxPropsAutoScrolItem(dimensionMemberOptions, optionValue.defaultFilterMember),
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={isFetchingDimensionMember ? 'Loading...' : dimensionFilterLabel}
                name='member'
              />
            )}
          />
        </Stack>
      )}
    </Stack>
  </Stack>
);

interface ChartSettingsProps {
  selectedView: PeopleMyViewItemConfig | null;
  optionValue: DefaultFilterDimensionValue;
  updateField: <K extends keyof DefaultFilterDimensionValue>(key: K, value: DefaultFilterDimensionValue[K]) => void;
  chartViewPanel: any;
  isFetchingMeasurement: boolean;
  isFetchingMember: boolean;
}

const ChartSettings: React.FC<ChartSettingsProps> = ({
  selectedView,
  optionValue,
  updateField,
  chartViewPanel,
  isFetchingMeasurement,
  isFetchingMember,
}) => (
  <Stack width='100%'>
    <Typography variant='input-label-gray' mb={2}>
      {`Set default chart measurment and default offset time period for ${selectedView?.componentName}.`}
    </Typography>
    <Stack direction='row' justifyContent='space-between' gap={2} alignItems='flex-start' width='100%'>
      <Stack width='50%'>
        <Autocomplete
          onChange={(event, value) => updateField('defaultMeasurement', value)}
          clearIcon={null}
          size='small'
          value={optionValue.defaultMeasurement || { label: 'Measurement', value: '' }}
          isOptionEqualToValue={(option: AutoCompleteItem, value: AutoCompleteItem) => option.value === value.value}
          disabled={isFetchingMeasurement}
          getOptionLabel={(option: AutoCompleteItem) => option.label}
          options={chartViewPanel?.data.measureOptions || []}
          renderOption={(props, option) => renderOption(props, option)}
          sx={autoCompleteStyle}
          renderInput={(params) => (
            <TextField
              {...params}
              label={isFetchingMember ? 'Loading...' : 'Measurement'}
              placeholder={isFetchingMember ? 'Loading...' : 'Select Measurement'}
              name='measureChartCubeAttribute'
            />
          )}
        />
      </Stack>
      <Stack width='50%'>
        <TextField
          name='search'
          size='small'
          value={optionValue.currentPeriodOffset}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            const regex = /^-?\d*$/;
            if (regex.test(event.target.value)) {
              updateField('currentPeriodOffset', Number(event.target.value));
            }
          }}
          placeholder='Enter current period adjustment'
          label='Current Period Adjustment'
          type='number'
          inputProps={{ step: 1 }}
        />
      </Stack>
    </Stack>
  </Stack>
);

interface TableAnalyticSettingProps {
  selectedView: PeopleMyViewItemConfig | null;
  optionValue: DefaultFilterDimensionValue;
  updateField: <K extends keyof DefaultFilterDimensionValue>(key: K, value: DefaultFilterDimensionValue[K]) => void;
  columnOptions: AutoCompleteItem[];
  rowOptions: AutoCompleteItem[];
  isFetchingMeasurement: boolean;
  isFetchingMember: boolean;
}

const TableAnalyticSetting: React.FC<TableAnalyticSettingProps> = ({
  selectedView,
  optionValue,
  updateField,
  columnOptions,
  rowOptions,
  isFetchingMeasurement,
  isFetchingMember,
}) => (
  <Stack width='100%'>
    <Typography variant='input-label-gray' mb={2}>
      {`Set default table analytic parameter for ${selectedView?.componentName}.`}
    </Typography>
    <Stack direction='row' justifyContent='space-between' gap={2} alignItems='flex-start' width='100%'>
      <Stack width='50%'>
        <Autocomplete
          onChange={(event, value) => updateField('tableAnalyticDefaultColumn', value)}
          clearIcon={null}
          size='small'
          value={optionValue.tableAnalyticDefaultColumn || { label: 'Measurement', value: '' }}
          isOptionEqualToValue={(option: AutoCompleteItem, value: AutoCompleteItem) => option.value === value.value}
          disabled={isFetchingMeasurement}
          getOptionLabel={(option: AutoCompleteItem) => option.label}
          options={columnOptions || []}
          renderOption={(props, option) => renderOption(props, option)}
          sx={autoCompleteStyle}
          renderInput={(params) => (
            <TextField
              {...params}
              label={isFetchingMember ? 'Loading...' : 'Default Selected Column'}
              placeholder={isFetchingMember ? 'Loading...' : 'Select Attribute'}
              name='tableAnalyticDefaultColumn'
            />
          )}
        />
      </Stack>
      <Stack width='50%'>
        <Autocomplete
          onChange={(event, value) => updateField('tableAnalyticDefaultRow', value)}
          clearIcon={null}
          size='small'
          value={optionValue.tableAnalyticDefaultRow || { label: 'Measurement', value: '' }}
          isOptionEqualToValue={(option: AutoCompleteItem, value: AutoCompleteItem) => option.value === value.value}
          disabled={isFetchingMeasurement}
          getOptionLabel={(option: AutoCompleteItem) => option.label}
          options={rowOptions || []}
          renderOption={(props, option) => renderOption(props, option)}
          sx={autoCompleteStyle}
          renderInput={(params) => (
            <TextField
              {...params}
              label={isFetchingMember ? 'Loading...' : 'Default Selected Row'}
              placeholder={isFetchingMember ? 'Loading...' : 'Select Attribute'}
              name='tableAnalyticDefaultRow'
            />
          )}
        />
      </Stack>
    </Stack>
  </Stack>
);

export function ModalMyViewAdditionalConfig(props: Readonly<ModalMyViewAdditionalConfigProps>) {
  const {
    onClose,
    onSubmit,
    visible = false,
    selectedView,
    defaultFilterMemberId,
    defaultXDimensionTypeId,
    defaultYDimensionTypeId,
  } = props;

  // Derived options and initial values
  const allDimensionOptions = useMemo(
    () => getInsightViewMeasureTypeDropdowns(selectedView?.ktreeName),
    [selectedView?.ktreeName]
  );

  const { data: chartViewPanel, isLoading: isFetchingMeasurement } = useGetChartViewPanelOptions(
    selectedView?.componentType === NAVIGATION_COMPONENT_TYPE.CHART ? selectedView?.componentId : undefined
  );

  const { data: tableAnalyticOptions } = useGetTableAnalyticOptions({
    enable: true,
    subjectId: selectedView?.componentId?.split('#')[0],
  });

  const initialDefaultYDimensionType = useMemo(() => {
    return allDimensionOptions.find((item) => item.value === INSIGHT_VIEW_MEASUREMENT_TYPE.INSIGHT_COLUMNS_MEMBER);
  }, [allDimensionOptions]);

  const initialDefaultXDimensionType = useMemo(() => {
    return allDimensionOptions.find((item) => item.value === INSIGHT_VIEW_MEASUREMENT_TYPE.MEASUREMENT_MEMBER);
  }, [allDimensionOptions]);

  const initialDefaultFilterValue = useMemo<DefaultFilterDimensionValue>(
    () => ({
      defaultFilterMember: null,
      defaultYDimensionType: initialDefaultYDimensionType || null,
      defaultXDimensionType: initialDefaultXDimensionType || null,
      member: null,
      additionalWorkFlows: null,
      defaultMeasurement: null,
      currentPeriodOffset: 0,
      tableAnalyticDefaultColumn:
        tableAnalyticOptions?.data?.columnOptions.find(
          (option) => option.value === selectedView?.tableAnalyticDefaultColumn
        ) || null,
      tableAnalyticDefaultRow:
        tableAnalyticOptions?.data?.rowOptions.find(
          (option) => option.value === selectedView?.tableAnalyticDefaultRow
        ) || null,
    }),
    [
      initialDefaultYDimensionType,
      initialDefaultXDimensionType,
      tableAnalyticOptions?.data,
      selectedView?.tableAnalyticDefaultColumn,
      selectedView?.tableAnalyticDefaultRow,
    ]
  );

  const [optionValue, setOptionValue] = useState<DefaultFilterDimensionValue>(initialDefaultFilterValue);

  // Update helper
  const updateField = <K extends keyof DefaultFilterDimensionValue>(key: K, value: DefaultFilterDimensionValue[K]) => {
    setOptionValue((prev) => ({ ...prev, [key]: value }));
  };

  // Data fetching hooks
  const { data: additionalWorkFlowsDropdown } = useGetAdditionalWorkFlowsDropdown();
  const { data: dimensionMemberDropdown, isLoading: isFetchingDimensionMember } = useGetCubeDimensionalViewFilterMember(
    'MAIN_MEMBER',
    selectedView?.cubeDefinitionId,
    optionValue.defaultXDimensionType?.value?.toString(),
    optionValue.defaultYDimensionType?.value?.toString(),
    DIMENSION_CATEGORY.CUBE_INSIGHT_VIEW,
    selectedView?.componentId
  );
  const { data: memberDropdown, isLoading: isFetchingMember } = useGetKtreeData(
    selectedView?.ktreeDefinitionId?.toString()
  );

  // Derived dropdown options
  const dimensionMemberOptions = useMemo(() => {
    return dimensionMemberDropdown ? flattenTreeData(dimensionMemberDropdown) : [];
  }, [dimensionMemberDropdown]);

  const memberOptions = useMemo(() => {
    return memberDropdown?.data ? flattenTreeData(memberDropdown.data) : [];
  }, [memberDropdown?.data]);

  const xDimensionOptions = useMemo(() => {
    return INSIGHT_VIEW_MEASURE_TYPE_DROPDOWN_OPTIONS.filter(
      (item) => item.value !== optionValue.defaultYDimensionType?.value
    ).map((item) =>
      item.value === INSIGHT_VIEW_MEASUREMENT_TYPE.MAIN_MEMBER && selectedView?.ktreeName
        ? { ...item, label: selectedView.ktreeName }
        : item
    );
  }, [optionValue.defaultYDimensionType, selectedView]);

  const yDimensionOptions = useMemo(() => {
    return INSIGHT_VIEW_MEASURE_TYPE_DROPDOWN_OPTIONS.filter(
      (item) => item.value !== optionValue.defaultXDimensionType?.value
    ).map((item) =>
      item.value === INSIGHT_VIEW_MEASUREMENT_TYPE.MAIN_MEMBER && selectedView?.ktreeName
        ? { ...item, label: selectedView.ktreeName }
        : item
    );
  }, [optionValue.defaultXDimensionType, selectedView]);

  const showDefaultDimensionMemberFilter = useMemo(() => {
    const excludeRule = new Set([
      INSIGHT_VIEW_MEASUREMENT_TYPE.INSIGHT_COLUMNS_MEMBER,
      INSIGHT_VIEW_MEASUREMENT_TYPE.MEASUREMENT_MEMBER,
    ]);
    return (
      !excludeRule.has(optionValue.defaultXDimensionType?.value as string) ||
      !excludeRule.has(optionValue.defaultYDimensionType?.value as string)
    );
  }, [optionValue.defaultXDimensionType, optionValue.defaultYDimensionType]);

  const lowestDimensionMemberLevel = useMemo(() => {
    // @ts-ignore
    return dimensionMemberOptions.reduce((acc, item) => (item.level > acc ? item.level : acc), 0);
  }, [dimensionMemberOptions]);

  const lowestMemberLevel = useMemo(() => {
    // @ts-ignore
    return memberOptions.reduce((acc, item) => (item.level > acc ? item.level : acc), 0);
  }, [memberOptions]);

  const dimensionFilterLabel = useMemo(() => {
    return getInsightViewDimensionFilterLabel(
      selectedView?.ktreeName,
      undefined,
      optionValue.defaultXDimensionType?.value.toString(),
      optionValue.defaultYDimensionType?.value.toString()
    );
  }, [optionValue.defaultXDimensionType, optionValue.defaultYDimensionType, selectedView?.ktreeName]);

  // Synchronize state when dependencies change
  useEffect(() => {
    setOptionValue(initialDefaultFilterValue);
  }, [initialDefaultFilterValue]);

  useEffect(() => {
    if (dimensionMemberOptions.length > 0) {
      updateField('defaultFilterMember', dimensionMemberOptions[0]);
    }
  }, [dimensionMemberOptions]);

  useEffect(() => {
    if (memberOptions.length && selectedView?.componentMemberId) {
      const selectedMember = memberOptions.find(
        (item: AutoCompleteItem) => item.value === selectedView.componentMemberId
      );
      if (selectedMember) {
        updateField('member', selectedMember);
      }
    }
  }, [memberOptions, selectedView?.componentMemberId, visible]);

  useEffect(() => {
    let selectedXValue: AutoCompleteItem | null = null;
    let selectedYValue: AutoCompleteItem | null = null;
    if (defaultXDimensionTypeId) {
      selectedXValue = allDimensionOptions.find((item) => item.value === defaultXDimensionTypeId) || null;
    }
    if (defaultYDimensionTypeId) {
      selectedYValue = allDimensionOptions.find((item) => item.value === defaultYDimensionTypeId) || null;
    }
    if (selectedXValue && selectedYValue) {
      updateField('defaultXDimensionType', selectedXValue);
      updateField('defaultYDimensionType', selectedYValue);
    }
  }, [defaultXDimensionTypeId, defaultYDimensionTypeId, allDimensionOptions]);

  useEffect(() => {
    if (defaultFilterMemberId) {
      const selectedFilterValue =
        dimensionMemberOptions.find((item: AutoCompleteItem) => item.value === defaultFilterMemberId) || null;
      if (selectedFilterValue) {
        updateField('defaultFilterMember', selectedFilterValue);
      }
    }
  }, [dimensionMemberOptions, defaultFilterMemberId]);

  useEffect(() => {
    if (selectedView?.additionalWorkFlows) {
      const additionalWorkFlowCube = selectedView.additionalWorkFlows?.[2];
      updateField(
        'additionalWorkFlows',
        additionalWorkFlowCube
          ? {
              label: additionalWorkFlowCube.componentName,
              value: additionalWorkFlowCube.componentId.toString(),
            }
          : null
      );
    }
  }, [selectedView?.additionalWorkFlows, visible]);

  useEffect(() => {
    if (selectedView?.defaultMeasurement && chartViewPanel?.data?.measureOptions) {
      const selectedMeasurement = chartViewPanel.data.measureOptions.find(
        (item: AutoCompleteItem) => item.value === selectedView.defaultMeasurement
      );
      updateField('defaultMeasurement', selectedMeasurement || null);
      updateField('currentPeriodOffset', selectedView.currentPeriodOffset);
    }
  }, [selectedView?.defaultMeasurement, selectedView?.currentPeriodOffset, chartViewPanel?.data?.measureOptions]);

  const isValidForm = optionValue.defaultXDimensionType !== null && optionValue.defaultYDimensionType !== null;

  const handleClose = () => {
    setOptionValue(initialDefaultFilterValue);
    onClose?.();
  };

  const handleSave = () => {
    if (selectedView) {
      const payload: RequestSaveMyViewAdditionalConfig = {
        myViewId: selectedView.id,
        defaultXDimension: optionValue.defaultXDimensionType?.value?.toString() || null,
        defaultYDimension: optionValue.defaultYDimensionType?.value?.toString() || null,
        defaultFilterMember: optionValue.defaultFilterMember?.value?.toString() || null,
        memberId: optionValue.member?.value?.toString() || null,
        memberName: optionValue.member?.label || null,
        additionalWorkFlows: optionValue.additionalWorkFlows ? [optionValue.additionalWorkFlows.value.toString()] : [],
        defaultMeasurement: optionValue.defaultMeasurement?.value?.toString() || null,
        currentPeriodOffset: optionValue.currentPeriodOffset || 0,
        tableAnalyticDefaultRow: optionValue.tableAnalyticDefaultRow?.value?.toString() || null,
        tableAnalyticDefaultColumn: optionValue.tableAnalyticDefaultColumn?.value?.toString() || null,
      };
      onSubmit(payload);
      handleClose();
      setOptionValue(initialDefaultFilterValue);
    }
  };

  return (
    <div>
      <Modal open={visible} onClose={handleClose}>
        <Stack direction='column' sx={ModalContentStyle} px={2} justifyContent='space-between'>
          <Stack>
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
              <Stack direction='row' alignItems='center' gap={1} flexWrap='wrap'>
                <Typography variant='body1' component='h2' fontWeight='bold' sx={{ color: '#3B4797' }}>
                  Page Settings
                </Typography>
                <Typography variant='body1' color='text.primary'>
                  — {selectedView?.componentName}
                </Typography>
                {selectedView?.componentType && (
                  <Chip
                    label={selectedView.componentType.replace(/_/g, ' ')}
                    size='small'
                    variant='outlined'
                    sx={{ fontSize: 11, height: 20, borderRadius: 1 }}
                  />
                )}
              </Stack>
              <IconButton onClick={handleClose} size='small'>
                <HighlightOffRoundedIcon sx={closeIconStyle} />
              </IconButton>
            </Stack>
            <Divider sx={{ mb: 1 }} />
            {selectedView?.componentType !== NAVIGATION_COMPONENT_TYPE.CHART &&
              selectedView?.componentType !== NAVIGATION_COMPONENT_TYPE.EVENT &&
              selectedView?.componentType !== NAVIGATION_COMPONENT_TYPE.TABLE_ANALYTIC && (
                <>
                  <Typography
                    variant='caption'
                    fontWeight={700}
                    color='text.secondary'
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.8, mt: 1 }}
                  >
                    View Settings
                  </Typography>
                  <Divider sx={{ mb: 1.5 }} />
                  <NonChartSettings
                  selectedView={selectedView}
                  optionValue={optionValue}
                  updateField={updateField}
                  memberOptions={memberOptions}
                  additionalWorkFlowsDropdown={additionalWorkFlowsDropdown}
                  xDimensionOptions={xDimensionOptions}
                  yDimensionOptions={yDimensionOptions}
                  dimensionMemberOptions={dimensionMemberOptions}
                  isFetchingMember={isFetchingMember}
                  isFetchingDimensionMember={isFetchingDimensionMember}
                  lowestMemberLevel={lowestMemberLevel}
                  lowestDimensionMemberLevel={lowestDimensionMemberLevel}
                  dimensionFilterLabel={dimensionFilterLabel}
                  showDefaultDimensionMemberFilter={showDefaultDimensionMemberFilter}
                />
                </>
              )}
            {selectedView?.componentType === NAVIGATION_COMPONENT_TYPE.CHART && (
              <>
                <Typography
                  variant='caption'
                  fontWeight={700}
                  color='text.secondary'
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.8, mt: 1 }}
                >
                  Chart Settings
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <ChartSettings
                selectedView={selectedView}
                optionValue={optionValue}
                updateField={updateField}
                chartViewPanel={chartViewPanel}
                isFetchingMeasurement={isFetchingMeasurement}
                isFetchingMember={isFetchingMember}
              />
              </>
            )}
            {selectedView?.componentType === NAVIGATION_COMPONENT_TYPE.TABLE_ANALYTIC && (
              <>
                <Typography
                  variant='caption'
                  fontWeight={700}
                  color='text.secondary'
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.8, mt: 1 }}
                >
                  Table Analytic Settings
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <TableAnalyticSetting
                selectedView={selectedView}
                optionValue={optionValue}
                updateField={updateField}
                columnOptions={tableAnalyticOptions?.data?.columnOptions || []}
                rowOptions={tableAnalyticOptions?.data?.rowOptions || []}
                isFetchingMeasurement={isFetchingMeasurement}
                isFetchingMember={isFetchingMember}
              />
              </>
            )}
          </Stack>
          <Stack px={2}>
            <Divider sx={{ mb: 1, mt: 2 }} />
            <Stack direction='row' justifyContent='flex-end' spacing={2} alignItems='center' sx={{ py: 1 }}>
              <Button variant='main-table-panel-border' onClick={handleClose}>
                Cancel
              </Button>
              <ButtonLoading variant='main-table-panel' disabled={!isValidForm} onClick={handleSave}>
                Update Setting
              </ButtonLoading>
            </Stack>
          </Stack>
        </Stack>
      </Modal>
    </div>
  );
}
